import { deleteDocumentById as deleteDocumentByIdApi, findDocuments, getDocument, getSignedDownloadUrl } from '@/api/basecamp.api';
import { updateDocumentRelation as updateDocumentRelationApi } from '@/api/basecamp.api';
import { deleteDataRoom, fetchDataRooms, saveDataRoom } from '@/api/data-room.api';
import { createLinkDocument, downloadFolder, getGoogleSheetMetaData } from '@/api/document.api';
import { updateMultipleDocuments as updateMultipleDocumentsApi } from '@/api/documents-folders.api';
import DocumentDownloadModal from '@/components/documents/DocumentDownloadModal.vue';
import DocumentSelect from '@/components/documents/DocumentSelect.vue';
import DocumentTreeSelector from '@/components/documents/DocumentTreeSelector.vue';
import FolderDownloadModal from '@/components/documents/FolderDownloadModal.vue';
import OfficeDocumentViewer from '@/components/documents/OfficeDocumentViewer.vue';
import PdfViewer from '@/components/documents/PdfViewer.vue';
import { OFFICE_DOCUMENT_EXTENSIONS } from '@/components/documents/documents-helpers';
import { confirmDialog, docTranslate, showErrorMessage, showModal } from '@/shared';
import type { IUploadData } from '@/store/modules/documents.store';
import {
    type DocumentType,
    DocumentUploadStatus,
    type ICreateLinkDocumentParams,
    type IDocument,
    type IDocumentFolder,
    type IEstate,
    type IFindDocumentsReq,
    type IGetDocumentParams,
    type IGoogleSheetMetaData,
    type IMFH,
    type ISaveDataRoom,
    type IUpdateMultipleDocumentsParams,
    type IUploadAndSaveDuplicateResult,
    type IUploadAndSaveFileResult,
} from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { saveAs } from 'file-saver';
import { pick, uniqBy } from 'lodash-es';
import { NButton, useDialog } from 'naive-ui';
import { type MaybeRef, type PropType, computed, defineComponent, h, ref, unref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

export const useDocument = (documentId: MaybeRef<number | null>, params?: MaybeRef<{ expand: IGetDocumentParams['expand'] }>) => {
    const enabled = computed(() => !!unref(documentId));
    return useQuery<IDocument>(['document', documentId], async () => getDocument(unref(documentId)!, params ? unref(params) : { expand: { select: ['*'] } }), {
        enabled,
    });
};

export const useDocuments = (params: MaybeRef<IFindDocumentsReq | undefined>) => {
    const enabled = computed(() => !!unref(params));
    return useQuery<IDocument[]>(['documents', params], async () => findDocuments(unref(params) as IFindDocumentsReq), {
        initialData: [],
        enabled,
    });
};

export const useDocumentMutations = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: updateMultipleDocuments } = useMutation({
        mutationFn: async (params: IUpdateMultipleDocumentsParams) => {
            await updateMultipleDocumentsApi(params);
            await queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });

    const { mutateAsync: deleteDocumentById } = useMutation({
        mutationFn: async (documentId: number) => {
            await deleteDocumentByIdApi(documentId);
            await queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });

    const { mutateAsync: deleteDocumentByIds } = useMutation({
        mutationFn: async (documentIds: number[]) => {
            await Promise.all(documentIds.map(id => deleteDocumentByIdApi(id)));
            await queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });

    const { mutateAsync: updateDocumentRelation } = useMutation({
        mutationFn: async (payload: {
            documentRelationId: number;
            body: {
                estateId: number;
                documentId: number;
                rentTenancyId?: number | null;
                investorInfoId?: number | null;
                serviceChargeStatementId?: number | null;
            };
        }) => {
            await updateDocumentRelationApi(payload.documentRelationId, payload.body);
            await queryClient.invalidateQueries({ queryKey: ['documents'] });
        },
    });

    return {
        updateMultipleDocuments,
        deleteDocumentById,
        deleteDocumentByIds,
        updateDocumentRelation,
    };
};

export const useGetDocument = (documentIdOrRef: MaybeRef<number>, paramsOrRef: MaybeRef<{ expand: IGetDocumentParams['expand'] }>) => {
    const data = useQuery<IDocument>(['get-document', documentIdOrRef], async () => {
        const documentId = computed(() => unref(documentIdOrRef));
        const params = computed(() => unref(paramsOrRef));
        return getDocument(documentId.value, params.value);
    });
    return { ...data };
};

export const useFindDocuments = (queryStringOrRef: MaybeRef<IFindDocumentsReq>) => {
    const enabled = computed(() => Object.entries(unref(queryStringOrRef).query).filter(([_, value]) => typeof value !== 'undefined').length > 0);
    return useQuery<IDocument[]>(['find-documents', queryStringOrRef], async () => findDocuments(unref(queryStringOrRef)), {
        initialData: [],
        enabled,
    });
};

export const useConfirmAndUploadDocument = () => {
    const store = useStore();
    const dialog = useDialog();
    const queryClient = useQueryClient();

    const confirmAndUpload = async ({ docData, fileOrFiles, relationData, folderId }: IUploadData) => {
        const uploadDocuments = (payload: IUploadData): Promise<IUploadAndSaveFileResult[]> => store.dispatch('documents/uploadDocuments', payload);

        const filesArray = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];

        const uploadParams: IUploadData = {
            fileOrFiles,
            relationData,
            ...(docData ? { docData: pick(docData, ['documentType', 'documentCategory']) } : {}),
            duplicateCheckConfig: { scope: 'global', mode: 'hashAndFilename' },
            displayDuplicateSnackbar: false,
            folderId,
        };
        const results = await uploadDocuments(uploadParams);

        for (let i = 0; i < results.length; i++) {
            const result = results[i];

            if (result.status === DocumentUploadStatus.Duplicate) {
                const duplicateResult = result as IUploadAndSaveDuplicateResult;
                if (
                    duplicateResult.duplicateDocuments?.some(
                        document => document.documentType === docData?.documentType && document.documentCategory === docData?.documentCategory,
                    )
                ) {
                    showErrorMessage({
                        message: `The same document "${filesArray[i]?.name || ''}" is uploaded under the same type, ${docTranslate(
                            docData?.documentType!,
                        )}. Please upload a different document.`,
                    });
                    continue;
                }
                const formattedContent = uniqBy(duplicateResult.duplicateDocuments, doc => `${doc.documentType}_${doc.documentCategory}`)
                    ?.map(doc => {
                        return `<p><b>${docTranslate(doc.documentType)} </b> of category ${doc.documentCategory}</p>`;
                    })
                    .join('');
                const uploadableFiles: File[] = [];
                await confirmDialog(
                    dialog,
                    {
                        content: `The same document "${filesArray[i]?.name || ''}" is uploaded under the type: ${formattedContent} <br>  Are you sure you would like to proceed with upload  ?`,
                    },
                    '',
                    async () => {
                        uploadableFiles.push(filesArray[i]);
                    },
                );

                await uploadDocuments({ ...uploadParams, fileOrFiles: uploadableFiles });
            }
        }

        await queryClient.invalidateQueries({ queryKey: ['documents'] });

        return results;
    };

    const confirmAndUploadLink = async (params: Partial<ICreateLinkDocumentParams>) => {
        const result = await createLinkDocument({ ...params, checkDuplicates: true } as ICreateLinkDocumentParams);
        if (result.status === DocumentUploadStatus.Duplicate) {
            const duplicateResult = result as IUploadAndSaveDuplicateResult;

            const formattedContent = uniqBy(duplicateResult.duplicateDocuments, doc => `${doc.documentType}_${doc.documentCategory}`)
                ?.map(doc => {
                    return `<p><b>${docTranslate(doc.documentType)} </b> of category ${doc.documentCategory}</p>`;
                })
                .join('');

            const res = await confirmDialog(dialog, {
                content: `The same document "${params.filename}" is uploaded under the type: ${formattedContent} <br>  Are you sure you would like to proceed with upload  ?`,
            });

            await createLinkDocument({ ...params, checkDuplicates: false } as ICreateLinkDocumentParams);
        }
        await queryClient.invalidateQueries({ queryKey: ['documents'] });

        return result;
    };

    return { confirmAndUpload, confirmAndUploadLink };
};

export const useDocumentFolderDownloader = () => {
    const downloadFolderAsZip = async (
        folderId: number,
        params: {
            estateId?: number;
            mfhId?: number;
            folderName: string;
        },
    ) => {
        const file = await downloadFolder(folderId, params);
        saveAs(file);
    };

    return {
        downloadFolderAsZip,
    };
};

export const useDeleteDocumentById = () => {
    const { mutateAsync: deleteDocumentById } = useMutation({
        mutationFn: async (documentId: number) => {
            return deleteDocumentByIdApi(documentId);
        },
    });
    return { deleteDocumentById };
};

export const useDataRoom = (paramsOrRef: MaybeRef<{ estateId?: number; mfhId?: number; userId?: string }>) => {
    const queryKey = ['data-room', unref(paramsOrRef).mfhId];

    return useQuery(queryKey, () => fetchDataRooms(unref(paramsOrRef)), {
        initialData: [],
        enabled: true,
    });
};

export const useDataRoomMutations = (paramsOrRef: MaybeRef<{ estateId?: number; mfhId?: number; userId?: string }>) => {
    const queryClient = useQueryClient();

    const queryKey = ['data-room', unref(paramsOrRef).mfhId];

    const { mutateAsync: createDataRoom } = useMutation({
        mutationFn: async (params: Partial<ISaveDataRoom>) => {
            await saveDataRoom(params);
            await queryClient.invalidateQueries(queryKey);
        },
    });

    const { mutateAsync: deleteDataRoomMutation } = useMutation({
        mutationFn: async (dataRoomId: number) => {
            await deleteDataRoom(dataRoomId);
            await queryClient.invalidateQueries(queryKey);
        },
    });

    return {
        createDataRoom,
        deleteDataRoom: deleteDataRoomMutation,
    };
};

export function useAttachments() {
    const router = useRouter();
    const VideoModal = defineComponent({
        name: 'VideoModal',
        props: {
            src: String,
        },
        setup(props) {
            return () =>
                h('video', {
                    src: props.src,
                    autoplay: true,
                    controls: true,
                    width: 2000,
                });
        },
    });

    const ImageModal = defineComponent({
        name: 'ImageModal',
        props: {
            src: String,
        },
        setup(props, { emit }) {
            return () =>
                h('img', {
                    src: props.src,
                    style: 'max-width: 90vw; max-height: 90vh; object-fit: contain; cursor: pointer;',
                    onClick: () => emit('close'),
                });
        },
    });

    const previewAttachment = async (document: IDocument) => {
        const { mimeType, link, path, isExternalLink, uuid, filename } = document;

        if (isExternalLink && link) {
            window.open(link);
            return;
        }

        const fileExtension = filename?.split('.').pop()?.toLowerCase() || '';
        const isOfficeDocument = OFFICE_DOCUMENT_EXTENSIONS.includes(fileExtension as (typeof OFFICE_DOCUMENT_EXTENSIONS)[number]);

        if (isOfficeDocument) {
            showModal(OfficeDocumentViewer, {
                props: { doc: document },
                width: 1600,
                maxWidth: 1600,
            });
            return;
        }

        switch (true) {
            case mimeType?.includes('video'): {
                showModal(VideoModal, {
                    props: { src: path },
                });
                break;
            }
            case mimeType?.includes('image'): {
                showModal(ImageModal, {
                    props: { src: path },
                });
                break;
            }
            case mimeType === 'application/pdf': {
                const docUrl = new URL(path);
                if (docUrl.hostname === 'drive.google.com') {
                    docUrl.searchParams.set('export', 'view');
                    window.open(docUrl.href);
                } else {
                    showModal(PdfViewer, {
                        props: { doc: document },
                    });
                }
                break;
            }
            default: {
                window.open(path);
            }
        }
    };

    const downloadAttachment = (documentOrFolder: IDocument | IDocumentFolder, entity?: { estate?: Partial<IEstate>; mfh?: Partial<IMFH> }) => {
        if ('documentId' in documentOrFolder) {
            return showModal(DocumentDownloadModal, {
                props: { document: documentOrFolder, estate: entity?.estate },
            });
        }

        return showModal(FolderDownloadModal, {
            props: {
                folder: documentOrFolder,
                estate: entity?.estate,
                mfh: entity?.mfh,
            },
        });
    };

    const openAttachment = (document: IDocument) => {
        const docUrl = new URL(document.path);
        if (docUrl.hostname === 'drive.google.com') {
            docUrl.searchParams.set('export', 'view');
            window.open(docUrl.href);
        } else {
            window.open(document.path);
        }
    };

    const previewSignedUrl = async (bucketFilename: string, filename: string, mimeType: string) => {
        const { url: downloadUrl } = await getSignedDownloadUrl(bucketFilename, filename);
        previewAttachment({ link: downloadUrl, path: downloadUrl, mimeType } as IDocument);
    };

    const openFileInDocumentLocation = async (document: IDocument, params: { estateId?: number; mfhId?: number }) => {
        let name = '';

        if (params.estateId) {
            name = 'one.estate.documents';
        } else if (params.mfhId) {
            name = 'one.mfh.documents';
        }

        const link = router.resolve({
            name,
            params: params,
            query: { folderId: document.folderId, documentId: document.documentId },
        });
        window.open(link.href, '_blank');
    };

    return {
        previewAttachment,
        downloadAttachment,
        openAttachment,
        previewSignedUrl,
        openFileInDocumentLocation,
    };
}

export function useGoogleSheetMetaData(spreadsheetUrl: MaybeRef<string>) {
    const enabled = computed(() => !!unref(spreadsheetUrl));
    return useQuery<Partial<IGoogleSheetMetaData>[]>({
        queryKey: ['google-sheet-metadata', spreadsheetUrl],
        queryFn: () => getGoogleSheetMetaData(unref(spreadsheetUrl)),
        enabled,
        initialData: [],
    });
}

export type DocumentSelectorParams = {
    title?: string;
    multiple?: boolean;
    estateId?: number;
    mfhId?: number;
    mfhIds?: number[];
    mimeType?: string;
    documentTypes?: DocumentType[];
    limit?: number;
};

export const useDocumentSelectorModal = () => {
    const DocumentSelectorModal = defineComponent({
        name: 'DocumentSelectorModal',
        props: {
            title: {
                type: String,
                default: 'Select Documents',
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            estateId: {
                type: Number,
                required: false,
            },
            mfhId: {
                type: Number,
                required: false,
            },
            mfhIds: {
                type: Array as PropType<number[]>,
                required: false,
            },
            mimeType: {
                type: String,
                required: false,
            },
            documentTypes: {
                type: Array as PropType<DocumentType[]>,
                required: false,
            },
            limit: {
                type: Number,
                required: false,
            },
        },
        emits: ['confirm', 'cancel'],
        setup(props, { emit }) {
            const selectedDocuments = ref<IDocument[]>([]);

            const handleDocumentsChange = (documents: IDocument[]) => {
                selectedDocuments.value = documents;
            };

            const confirmSelection = () => {
                emit('confirm', selectedDocuments.value);
            };

            const cancelSelection = () => {
                emit('cancel');
            };

            return () =>
                h('div', { class: 'w-full rounded-lg bg-white p-6' }, [
                    h('h3', { class: 'mb-4 text-lg font-semibold' }, props.title),
                    h(DocumentSelect, {
                        estateId: props.estateId,
                        mfhId: props.mfhId,
                        mfhIds: props.mfhIds,
                        multiple: props.multiple,
                        mimeType: props.mimeType,
                        documentTypes: props.documentTypes,
                        limit: props.limit,
                        'onUpdate:documents': handleDocumentsChange,
                    }),
                    h('div', { class: 'mt-4 flex justify-end gap-2' }, [
                        h(NButton, { onClick: cancelSelection, size: 'small' }, 'Cancel'),
                        h(
                            NButton,
                            {
                                onClick: confirmSelection,
                                disabled: !selectedDocuments.value.length,
                                size: 'small',
                                type: 'primary',
                            },
                            'Confirm',
                        ),
                    ]),
                ]);
        },
    });

    const openDocumentSelector = (params?: DocumentSelectorParams): Promise<{ documents: IDocument[]; close: () => void } | null> => {
        return new Promise(resolve => {
            const modalInstance = showModal(DocumentSelectorModal, {
                width: 600,
                maxWidth: 600,
                props: {
                    title: params?.title || 'Select Documents',
                    multiple: params?.multiple ?? false,
                    estateId: params?.estateId,
                    mfhId: params?.mfhId,
                    mfhIds: params?.mfhIds,
                    mimeType: params?.mimeType,
                    documentTypes: params?.documentTypes,
                    limit: params?.limit,
                },
                events: {
                    confirm: (documents: IDocument[]) => {
                        resolve({
                            documents,
                            close: () => modalInstance.close(),
                        });
                    },
                    cancel: () => {
                        modalInstance.close();
                        resolve(null);
                    },
                },
            });
        });
    };

    return { openDocumentSelector };
};

export type DocumentTreeSelectorParams = {
    title?: string;
    estateId?: number;
    mfhId?: number;
    mfhIds?: number[];
    maxWidth?: string;
    mimeType?: string;
};

export const useDocumentTreeSelectorModal = () => {
    const DocumentTreeSelectorModal = defineComponent({
        name: 'DocumentTreeSelectorModal',
        props: {
            title: {
                type: String,
                default: 'Select Documents and Folders',
            },
            estateId: {
                type: Number,
                required: false,
            },
            mfhId: {
                type: Number,
                required: false,
            },
            maxWidth: {
                type: String,
                required: false,
            },
            mimeType: {
                type: String,
                required: false,
            },
        },
        emits: ['confirm', 'cancel'],
        setup(props, { emit }) {
            const selectedDocuments = ref<IDocument[]>([]);

            const handleDocumentsChange = (documents: IDocument[]) => {
                selectedDocuments.value = documents;
            };

            const confirmSelection = () => {
                emit('confirm', {
                    documents: selectedDocuments.value,
                });
            };

            const cancelSelection = () => {
                emit('cancel');
            };

            return () =>
                h('div', { class: 'w-full rounded-lg bg-white p-6' }, [
                    h('h3', { class: 'mb-4 text-lg font-semibold' }, props.title),
                    h(DocumentTreeSelector, {
                        estateId: props.estateId,
                        mfhId: props.mfhId,
                        mimeType: props.mimeType,
                        maxWidth: props.maxWidth,
                        'onUpdate:documents': handleDocumentsChange,
                    }),
                    h('div', { class: 'mt-4 flex justify-end gap-2' }, [
                        h(NButton, { onClick: cancelSelection, size: 'small' }, 'Cancel'),
                        h(
                            NButton,
                            {
                                onClick: confirmSelection,
                                size: 'small',
                                type: 'primary',
                            },
                            'Confirm',
                        ),
                    ]),
                ]);
        },
    });

    const openDocumentTreeSelector = (params?: DocumentTreeSelectorParams): Promise<{ documents: IDocument[]; close: () => void } | null> => {
        return new Promise(resolve => {
            const modalInstance = showModal(DocumentTreeSelectorModal, {
                width: 700,
                maxWidth: 700,
                props: {
                    title: params?.title || 'Select Documents',
                    estateId: params?.estateId,
                    mfhId: params?.mfhId,
                    mfhIds: params?.mfhIds,
                    maxWidth: params?.maxWidth,
                    mimeType: params?.mimeType,
                },
                events: {
                    confirm: (result: { documents: IDocument[] }) => {
                        resolve({
                            documents: result.documents,
                            close: () => modalInstance.close(),
                        });
                    },
                    cancel: () => {
                        modalInstance.close();
                        resolve(null);
                    },
                },
            });
        });
    };

    return { openDocumentTreeSelector };
};
